import { useState } from "react";
import { Link } from "react-router-dom";
import { useCreateEmployee, useEmployerEmployees } from "@/hooks/use-hr-queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, initials, Page, Panel, QueryGate, StatusBadge } from "@/components/shell/page";

const fields = [
  ["employee_code", "Employee ID"],
  ["name", "Name"],
  ["email", "Email"],
  ["password", "Temporary password"],
  ["designation", "Designation"],
  ["device_pin", "Device PIN (optional)"],
] as const;

export default function EmployerEmployeesPage() {
  const { data, isLoading, error } = useEmployerEmployees();
  const create = useCreateEmployee();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    employee_code: "",
    name: "",
    email: "",
    password: "",
    designation: "",
    device_pin: "",
  });

  return (
    <QueryGate loading={isLoading} error={error || !data ? "Could not load employees." : null}>
      {data && (
        <Page
          title="Employees"
          description="Directory of staff records. Open someone to see their day without using their password."
          actions={
            <Button onClick={() => setOpen((value) => !value)}>
              {open ? "Close form" : "Add employee"}
            </Button>
          }
        >
          {open && (
            <Panel title="New employee">
              <div className="grid gap-3 sm:grid-cols-2">
                {fields.map(([key, label]) => (
                  <div key={key} className="space-y-1">
                    <Label htmlFor={key}>{label}</Label>
                    <Input
                      id={key}
                      type={key === "password" ? "password" : "text"}
                      autoComplete={key === "password" ? "new-password" : "off"}
                      value={form[key]}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    />
                  </div>
                ))}
                <Button
                  className="sm:col-span-2"
                  disabled={create.isPending}
                  onClick={() => create.mutate(form, { onSuccess: () => setOpen(false) })}
                >
                  {create.isPending ? "Creating…" : "Create employee"}
                </Button>
              </div>
            </Panel>
          )}

          {data.length === 0 && (
            <EmptyState title="No employees yet" body="Add the first staff record to start leave, tasks, and hours." />
          )}

          {data.length > 0 && (
            <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="border-b bg-muted/60 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="hidden px-4 py-3 font-medium sm:table-cell">Role</th>
                    <th className="hidden px-4 py-3 font-medium md:table-cell">PIN</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {data.map((person: any) => (
                    <tr key={person.id} className="border-b last:border-0">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                            {initials(person.name)}
                          </span>
                          <div>
                            <p className="font-medium">{person.name}</p>
                            <p className="text-xs text-muted-foreground">{person.employee_code}</p>
                          </div>
                        </div>
                      </td>
                      <td className="hidden px-4 py-3 text-muted-foreground sm:table-cell">
                        {person.designation || "—"}
                      </td>
                      <td className="hidden px-4 py-3 text-muted-foreground md:table-cell">
                        {person.device_pin || "Not linked"}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={person.employment_status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/employer/employees/${person.id}`}>Open</Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Page>
      )}
    </QueryGate>
  );
}
